import { Button, Card, isSafeCssColor, useToast } from '@citizen-walk/shared-ui';
import {
  categoryMasterAllModes,
  type AiCategoryChainGlobalRules,
  type AiCategoryMaster,
  type AiCategoryMasterRow,
  type DistrictMode,
} from '@citizen-walk/shared-types';
import { useRef, useState } from 'react';
import { ColorSwatch } from './components/ColorSwatch';
import { ModeMultiSelectField } from './components/ModeSelectField';
import { archiveMaster, upsertMaster, useAiCategoryMasters } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import {
  collectOptionalColumnKeys,
  formatOptionalCellValue,
  isCoreRowColumn,
  parseCategoryRowsFromCsv,
  parseOptionalCellValue,
  serializeCategoryRowsToCsv,
} from './csvImportExport';
import { stripSystemFields } from './jsonImportExport';

/**
 * M2 aiCategoryMasters 管理画面 (Phase 3.5 admin UI 第 1 弾 + rows/colorMap 編集追加)。
 *
 * 旧 admin app の MasterListPage / MasterEditPage を統合した実装。
 *
 * 残作業 (docs/98 R-29):
 *   - history 表示 (onMasterWrite trigger 由来の `/aiCategoryMasters/{id}/history`)
 *   - 残 master (M1 / M4 / M6 / M7 / M8) の個別 page (array of struct 等の複雑 schema)
 *   - master role 認証 (Phase 5 admin app の auth flow)
 *   - version 自動 increment
 *
 * 現状は emulator + seed script で role: 'master' を付与する前提で動作確認。
 */

const COLLECTION_NAME = 'aiCategoryMasters';

/** colorMap は内部編集中は順序保持のため key/value pair の配列で扱う (Save 時に Record 化)。 */
interface ColorMapEntry {
  key: string;
  value: string;
}

interface FormDraft {
  id: string;
  displayName: string;
  /** Sprint BG-DataSettings 25 — 複数 mode 対応。modes[0] が primary mode として保存される。 */
  modes: DistrictMode[];
  templateVar: string;
  /** 動的管理拡張: 軸 dropdown 等で使う日本語表示名 (空文字なら未指定として書込まない)。 */
  displayLabelJa: string;
  definition: string;
  usageRules: string;
  colorAxis: AiCategoryMaster['colorAxis'];
  rows: AiCategoryMasterRow[];
  colorMapEntries: ColorMapEntry[];
  /**
   * rows[] の categoryCd/category/majorCd/major/color/example/definition/criteria
   * (=常設の core column) 以外に実データへ現れている optional 列の key 一覧。
   * gqlCodes / chainRules / icon 等、マスタ (JSON フォーマット) ごとに異なりうる列は
   * コード側に列名をハードコードせず、この一覧に基づいて動的にテーブル列を描画する。
   */
  optionalColumnKeys: string[];
  /**
   * docs/105 M2 連鎖ルール (マスタ全体)。編集中は入力しやすいよう
   * 配列はカンマ区切り文字列で保持し、Save 時に AiCategoryChainGlobalRules へ変換する。
   * いずれも空なら chainGlobalRules を書込まない (optional)。
   */
  chainForbiddenAsPrimary: string;
  chainForbiddenAsSub: string;
  chainGlobalNote: string;
}

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  modes: ['ecw'],
  templateVar: '',
  displayLabelJa: '',
  definition: '',
  usageRules: '',
  colorAxis: 'category',
  rows: [],
  colorMapEntries: [],
  optionalColumnKeys: [],
  chainForbiddenAsPrimary: '',
  chainForbiddenAsSub: '',
  chainGlobalNote: '',
};

/**
 * 行追加時の初期値。majorCd / major は optional のため省略 (`undefined`)、color は中性
 * グレーで埋めておく (colorAxis = 'category' / 'categoryCd' 時の凡例品質リスクは R-29 で
 * 「color 未設定行の警告 UI」検討項目に記録)。
 */
const EMPTY_ROW: AiCategoryMasterRow = {
  categoryCd: '',
  category: '',
  color: '#888888',
};

const INPUT_CLASS =
  'w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-400';

function colorMapToEntries(map: Record<string, string>): ColorMapEntry[] {
  return Object.entries(map).map(([key, value]) => ({ key, value }));
}

function entriesToColorMap(entries: ColorMapEntry[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const { key, value } of entries) {
    const k = key.trim();
    if (k) result[k] = value;
  }
  return result;
}

/** カンマ区切り文字列 → trim + 空要素除去した配列。 */
function splitCodes(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/** colorAxis に応じて rows[] から colorMap key 候補を抽出 (重複除去 + 空白除外)。 */
function deriveColorMapKeys(
  rows: AiCategoryMasterRow[],
  colorAxis: AiCategoryMaster['colorAxis'],
): string[] {
  if (colorAxis === 'category' || colorAxis === 'categoryCd') return [];
  const field = colorAxis; // 'major' | 'majorCd'
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    const v = (row[field] ?? '').trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * AiCategoryMaster validator (M2 JSON import 用、minimum 必須 field のみ check)。
 */
function validateAiCategoryMaster(raw: unknown): (AiCategoryMaster & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  if (!Array.isArray(r.rows)) return null;
  return r as unknown as AiCategoryMaster & { id: string };
}

export function AiCategoryMastersPage() {
  const state = useAiCategoryMasters();
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);
  // Sprint BG-MasterIO follow-up — rows[] CSV 読込 input への参照 (document.getElementById から useRef に統一)
  const csvInputRef = useRef<HTMLInputElement | null>(null);
  // 追加する optional 列名の入力中の値 (「＋ 列追加」フォーム用、保存対象ではない一時 UI state)。
  const [newColumnName, setNewColumnName] = useState('');

  async function handleImport(importedItems: (AiCategoryMaster & { id: string })[]) {
    for (const it of importedItems) {
      const existing =
        state.status === 'ready' ? state.items.find((x) => x.id === it.id) : undefined;
      // Sprint BG-MasterIO follow-up — system field を除去して Firestore Timestamp 上書き防止
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<AiCategoryMaster>({
        collectionName: COLLECTION_NAME,
        id: id as string,
        uid: 'admin-stub',
        isNew: !existing,
        data: rest as Omit<
          AiCategoryMaster,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  // M2 専用: 編集中の master の rows[] のみ CSV で I/O。
  function handleRowsCsvExport() {
    if (!editing) return;
    try {
      // optionalColumnKeys を明示的に渡し、値が全行空でも列として出力する (追加直後の列も往復可能)。
      const csv = serializeCategoryRowsToCsv(editing.rows, editing.optionalColumnKeys);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `${editing.id || 'aiCategoryMaster'}-rows-${new Date().toISOString().slice(0, 10)}.csv`;
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast('success', `${editing.rows.length} 行を ${filename} に書き出しました`);
    } catch (e) {
      toast('error', `CSV 書き出し失敗: ${(e as Error).message}`);
    }
  }

  async function handleRowsCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editing) return;
    try {
      const text = await file.text();
      const { rows, optionalColumns, errors } = parseCategoryRowsFromCsv(text);
      if (rows.length === 0) {
        toast('error', `取込可能な行がありません (失敗 ${errors.length} 件)`);
        return;
      }
      const msg =
        errors.length > 0
          ? `${rows.length} 行を取込みます (失敗 ${errors.length} 件は skip)。\n${errors.slice(0, 3).join('\n')}\n\n既存 rows[] を置換しますか?`
          : `${rows.length} 行を取込み既存 rows[] を置換しますか?`;
      if (!confirm(msg)) return;
      // CSV は rows[] を全置換するので、optional 列一覧も今回の CSV header から再構築する。
      setEditing({ ...editing, rows, optionalColumnKeys: optionalColumns });
      toast('success', `${rows.length} 行を取込みました (未保存)`);
    } catch (err) {
      toast('error', `CSV 取込失敗: ${(err as Error).message}`);
    }
  }

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT, rows: [], colorMapEntries: [] });
    setIsNew(true);
  }

  function handleEdit(item: AiCategoryMaster & { id: string }) {
    const g = item.chainGlobalRules;
    setEditing({
      id: item.id,
      displayName: item.displayName,
      modes: categoryMasterAllModes(item),
      templateVar: item.templateVar,
      displayLabelJa: item.displayLabelJa ?? '',
      definition: item.definition,
      usageRules: item.usageRules,
      colorAxis: item.colorAxis,
      // rows[] は gqlCodes / chainRules 等の optional field を含め、そのまま引継ぐ。
      rows: item.rows.map((r) => ({ ...r })),
      colorMapEntries: colorMapToEntries(item.colorMap),
      // 実データに現れている optional 列 (列名は固定コードではなく実データ由来) を検出する。
      optionalColumnKeys: collectOptionalColumnKeys(item.rows),
      chainForbiddenAsPrimary: (g?.forbiddenAsPrimary ?? []).join(', '),
      chainForbiddenAsSub: (g?.forbiddenAsSub ?? []).join(', '),
      chainGlobalNote: g?.note ?? '',
    });
    setIsNew(false);
  }

  function toggleMode(mode: DistrictMode) {
    if (!editing) return;
    const next = editing.modes.includes(mode)
      ? editing.modes.filter((m) => m !== mode)
      : [...editing.modes, mode];
    setEditing({ ...editing, modes: next });
  }

  function addRow() {
    if (!editing) return;
    setEditing({ ...editing, rows: [...editing.rows, { ...EMPTY_ROW }] });
  }

  /** `patch` は core field に加え、実データ駆動の optional 列 key も受け付ける。 */
  function updateRow(index: number, patch: Record<string, unknown>) {
    if (!editing) return;
    const target = editing.rows[index];
    if (!target) return;
    const next = [...editing.rows];
    next[index] = { ...target, ...patch } as unknown as AiCategoryMasterRow;
    setEditing({ ...editing, rows: next });
  }

  function removeRow(index: number) {
    if (!editing) return;
    setEditing({ ...editing, rows: editing.rows.filter((_, i) => i !== index) });
  }

  /**
   * optional 列 (JSON フォーマット依存、任意のキー名) を追加する。列名はコード側にハードコードせず
   * 管理者が入力した文字列をそのまま key として使う。既存 rows[] の値には影響しない。
   */
  function addOptionalColumn(rawKey: string) {
    if (!editing) return;
    const key = rawKey.trim();
    if (!key) {
      toast('error', '列名を入力してください');
      return;
    }
    if (isCoreRowColumn(key) || editing.optionalColumnKeys.includes(key)) {
      toast('error', `列「${key}」は既に存在します`);
      return;
    }
    setEditing({ ...editing, optionalColumnKeys: [...editing.optionalColumnKeys, key] });
  }

  /** optional 列を削除する (一覧から外し、全 rows[] から該当 key を除去する)。 */
  function removeOptionalColumn(key: string) {
    if (!editing) return;
    if (!confirm(`列「${key}」を削除しますか? (全行のこの列のデータが削除されます)`)) return;
    const nextRows = editing.rows.map((row) => {
      const r = { ...(row as unknown as Record<string, unknown>) };
      delete r[key];
      return r as unknown as AiCategoryMasterRow;
    });
    setEditing({
      ...editing,
      rows: nextRows,
      optionalColumnKeys: editing.optionalColumnKeys.filter((k) => k !== key),
    });
  }

  function addColorMapEntry() {
    if (!editing) return;
    setEditing({
      ...editing,
      colorMapEntries: [...editing.colorMapEntries, { key: '', value: '#888888' }],
    });
  }

  function updateColorMapEntry(index: number, patch: Partial<ColorMapEntry>) {
    if (!editing) return;
    const target = editing.colorMapEntries[index];
    if (!target) return;
    const next = [...editing.colorMapEntries];
    next[index] = { ...target, ...patch };
    setEditing({ ...editing, colorMapEntries: next });
  }

  function removeColorMapEntry(index: number) {
    if (!editing) return;
    setEditing({
      ...editing,
      colorMapEntries: editing.colorMapEntries.filter((_, i) => i !== index),
    });
  }

  /** rows[] の major / majorCd から colorMap key 候補を取り込む (既存 key は保持)。 */
  function syncColorMapFromRows() {
    if (!editing) return;
    const derivedKeys = deriveColorMapKeys(editing.rows, editing.colorAxis);
    const existingKeys = new Set(editing.colorMapEntries.map((e) => e.key.trim()));
    const added: ColorMapEntry[] = [];
    for (const k of derivedKeys) {
      if (!existingKeys.has(k)) added.push({ key: k, value: '#888888' });
    }
    if (added.length === 0) {
      toast('info', '追加対象の key はありません (既に全て登録済)');
      return;
    }
    setEditing({ ...editing, colorMapEntries: [...editing.colorMapEntries, ...added] });
    toast('success', `${added.length} 件の key を追加しました`);
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    if (isNew && !editing.id.trim()) {
      toast('error', '新規時は id (kebab-case) を指定してください');
      return;
    }
    if (editing.modes.length === 0) {
      toast('error', 'mode は 1 つ以上選択してください');
      return;
    }
    // rows[] バリデーション: categoryCd / category は必須 (docs/04 §3-3 M2 AiCategoryMasterRow)
    const cleanedRows: AiCategoryMasterRow[] = [];
    let rowIndex = 0;
    for (const r of editing.rows) {
      rowIndex++;
      if (!r.categoryCd.trim() || !r.category.trim()) {
        toast('error', `行 ${rowIndex}: categoryCd と category は必須です`);
        return;
      }
      const row: Record<string, unknown> = {
        categoryCd: r.categoryCd.trim(),
        category: r.category.trim(),
      };
      if (r.majorCd?.trim()) row.majorCd = r.majorCd.trim();
      if (r.major?.trim()) row.major = r.major.trim();
      if (r.color?.trim()) {
        // 色は地図の SVG / style に埋め込まれるため、色として解釈できる値だけを保存する (docs/13 §7)。
        if (!isSafeCssColor(r.color)) {
          toast('error', `行 ${rowIndex}: 色は #RRGGBB の形式で指定してください`);
          return;
        }
        row.color = r.color.trim();
      }
      // example / definition / criteria は AI 分類精度を左右する core field (docs/105 A-②)。
      if (r.example?.trim()) row.example = r.example.trim();
      if (r.definition?.trim()) row.definition = r.definition.trim();
      if (r.criteria?.trim()) row.criteria = r.criteria.trim();
      // docs/25 §10-5 拡張: エリア/路線集計から除外するフラグ (既定 false は書込まない)。
      if (r.excludeFromAggregation) row.excludeFromAggregation = true;
      // docs/27 §9: 評価の極性 (未設定は書込まない)。
      if (r.polarity) row.polarity = r.polarity;
      // gqlCodes / chainRules 等の optional field はマスタごとに異なるため、列名をハードコードせず
      // 実データ駆動の optionalColumnKeys に基づきそのまま引継ぐ。
      for (const key of editing.optionalColumnKeys) {
        const v = (r as unknown as Record<string, unknown>)[key];
        if (v !== undefined && v !== null && v !== '') row[key] = v;
      }
      cleanedRows.push(row as unknown as AiCategoryMasterRow);
    }
    for (const entry of editing.colorMapEntries) {
      if (entry.key.trim() && !isSafeCssColor(entry.value)) {
        toast('error', `色の対応「${entry.key.trim()}」: 色は #RRGGBB の形式で指定してください`);
        return;
      }
    }
    // docs/105 M2: マスタ全体 chainGlobalRules を組立 (全フィールド空なら undefined)。
    const chainGlobalRules: AiCategoryChainGlobalRules = {};
    const fp = splitCodes(editing.chainForbiddenAsPrimary);
    const fs = splitCodes(editing.chainForbiddenAsSub);
    const gnote = editing.chainGlobalNote.trim();
    if (fp.length > 0) chainGlobalRules.forbiddenAsPrimary = fp;
    if (fs.length > 0) chainGlobalRules.forbiddenAsSub = fs;
    if (gnote) chainGlobalRules.note = gnote;
    const hasChainGlobal = Object.keys(chainGlobalRules).length > 0;
    setSaving(true);
    try {
      // Sprint BG-DataSettings 25: `mode` (primary、旧 schema 互換) + `modes` (all、新) を併記。
      //   - 単一モードでも `modes: ['ecw']` を書込んで読込側の判定を 1 経路化
      //   - mode は modes[0] 兼用
      const modes = [...editing.modes];
      // 動的管理拡張: displayLabelJa は空文字なら書込まない (optional)。
      const displayLabelJa = editing.displayLabelJa.trim();
      await upsertMaster<AiCategoryMaster>({
        collectionName: COLLECTION_NAME,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data: {
          mode: modes[0]!,
          modes,
          displayName: editing.displayName.trim(),
          templateVar: editing.templateVar.trim(),
          ...(displayLabelJa ? { displayLabelJa } : {}),
          definition: editing.definition,
          usageRules: editing.usageRules,
          colorAxis: editing.colorAxis,
          rows: cleanedRows,
          colorMap: entriesToColorMap(editing.colorMapEntries),
          ...(hasChainGlobal ? { chainGlobalRules } : {}),
          enabled: true,
          version: 1,
          schemaVersion: 1,
          archivedAt: null,
        },
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      setEditing(null);
      setIsNew(false);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(COLLECTION_NAME, id, 'admin-stub'); // Phase 5 で実 master role uid に置換
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  const colorMapVisible =
    editing && (editing.colorAxis === 'major' || editing.colorAxis === 'majorCd');

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">AI カテゴリマスター</h1>
        <p className="text-sm text-slate-500 mt-1">
          AI 分類軸 (内部 ID: aiCategoryMasters、docs/04 §3-3 M2)。Phase 3.5 admin 第 1 弾 +
          rows/colorMap 編集。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<AiCategoryMaster & { id: string }>
          collectionName={COLLECTION_NAME}
          items={state.status === 'ready' ? state.items : []}
          validator={validateAiCategoryMaster}
          onImport={handleImport}
          disabled={editing !== null}
        />
      </div>

      {state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {state.error.message}</p>
      )}
      {state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {state.items.length} (アーカイブ済除外)
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {state.items.map((item) => (
              <li key={item.id}>
                <Card padding="md" className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <strong className="text-slate-900">{item.displayName}</strong>
                    <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                    <span className="ml-2 text-xs text-slate-500">
                      mode: {categoryMasterAllModes(item).join(', ')}
                    </span>
                    <span className="ml-2 text-xs text-slate-500">rows: {item.rows.length}</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <RowExportButton collectionName={COLLECTION_NAME} item={item} />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      aria-label={`${item.displayName} を編集`}
                    >
                      編集
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                      aria-label={`${item.displayName} の履歴を表示`}
                    >
                      履歴
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleArchive(item.id, item.displayName)}
                      aria-label={`${item.displayName} をアーカイブ`}
                    >
                      アーカイブ
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={COLLECTION_NAME}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          <div className="grid grid-cols-1 gap-3">
            {isNew && (
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-id">
                  ドキュメント ID (id) — kebab-case、保存後変更不可{' '}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  id="m2-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder="例: ecw-default"
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-name">
                表示名称 (displayName) <span className="text-danger-600">*</span>
              </label>
              <input
                id="m2-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <span className="block text-xs text-slate-600 mb-1">
                モード (mode、利用する全 mode を選択、複数可){' '}
                <span className="text-danger-600">*</span>
              </span>
              {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成。
                  現在値が未登録 mode (例: legacy custom mode) でも「(未登録)」で必ず表示される。 */}
              <ModeMultiSelectField values={editing.modes} onToggle={toggleMode} />
              <p className="text-[10px] text-slate-400 mt-1">
                ※ 複数チェックすると、それぞれの mode の M1 (aiAnalysisSettings) 編集画面で
                候補として表示されます。 primary mode は先頭にチェックを入れたものです。 新しい mode
                は「モードプロファイル」画面で登録してください。
              </p>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-tplvar">
                プロンプト埋込変数名 (templateVar)
              </label>
              <input
                id="m2-tplvar"
                type="text"
                value={editing.templateVar}
                onChange={(e) => setEditing({ ...editing, templateVar: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="例: ecwCategories"
              />
            </div>
            {/* 動的管理拡張: displayLabelJa — 軸 dropdown 等の表示ラベル */}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-display-label">
                軸の日本語表示名 (displayLabelJa、軸 dropdown 等の表示名)
              </label>
              <input
                id="m2-display-label"
                type="text"
                value={editing.displayLabelJa}
                onChange={(e) => setEditing({ ...editing, displayLabelJa: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="未入力なら templateVar から自動導出"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                ※ 分析アプリの軸選択 dropdown 等で使う日本語表示名。例: emotionMaster → 「感情」 /
                categoryMaster → 「対象物カテゴリ」 / safetyMaster → 「安全性」。 未入力なら
                templateVar の root から fallback 辞書 → raw key の順で導出。
              </p>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-def">
                定義 (definition、AI 定義文)
              </label>
              <textarea
                id="m2-def"
                value={editing.definition}
                onChange={(e) => setEditing({ ...editing, definition: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-usage">
                利用ルール (usageRules、AI 使用ルール文)
              </label>
              <textarea
                id="m2-usage"
                value={editing.usageRules}
                onChange={(e) => setEditing({ ...editing, usageRules: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-coloraxis">
                色分け軸 (colorAxis)
              </label>
              <select
                id="m2-coloraxis"
                value={editing.colorAxis}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    colorAxis: e.target.value as AiCategoryMaster['colorAxis'],
                  })
                }
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="category">category (rows[].color を使用)</option>
                <option value="categoryCd">categoryCd (rows[].color を使用)</option>
                <option value="major">major (colorMap を使用)</option>
                <option value="majorCd">majorCd (colorMap を使用)</option>
              </select>
            </div>

            {/* rows[] 編集 */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <label className="block text-sm font-bold text-slate-700">
                  rows[] (分類行、{editing.rows.length} 件)
                </label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRowsCsvExport}
                    disabled={editing.rows.length === 0}
                    aria-label="rows[] を CSV ダウンロード"
                  >
                    📥 CSV ダウンロード
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => csvInputRef.current?.click()}
                    aria-label="CSV から rows[] を読込"
                  >
                    📤 CSV 読込
                  </Button>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => void handleRowsCsvImport(e)}
                  />
                  <Button variant="secondary" size="sm" onClick={addRow}>
                    ＋ 行追加
                  </Button>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      className={`${INPUT_CLASS} w-32`}
                      placeholder="列名 (例: gqlCodes)"
                      aria-label="追加する optional 列名"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        addOptionalColumn(newColumnName);
                        setNewColumnName('');
                      }}
                    >
                      ＋ 列追加(optional)
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mb-2">
                categoryCd 〜 比較除外 は常設の必須列。gqlCodes / chainRules 等の optional
                列はマスタ (JSON フォーマット) ごとに異なるため、実データまたは CSV header
                から動的に検出されます。値は文字列ならそのまま、配列・オブジェクトは JSON
                形式で入力してください (例: {'["GQL-04","GQL-01"]'})。
              </p>
              {editing.rows.length === 0 ? (
                <p className="text-xs text-slate-500">行がありません。「＋ 行追加」で追加。</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-left text-slate-600 border-b border-slate-200">
                        <th className="py-1 pr-2 font-medium">
                          categoryCd <span className="text-danger-600">*</span>
                        </th>
                        <th className="py-1 pr-2 font-medium">
                          category <span className="text-danger-600">*</span>
                        </th>
                        <th className="py-1 pr-2 font-medium">majorCd</th>
                        <th className="py-1 pr-2 font-medium">major</th>
                        <th className="py-1 pr-2 font-medium">color</th>
                        {/* example / definition / criteria: AI プロンプトの分類精度を左右する
                            必須情報 (docs/105 A-②)。常設の core column として常に表示する。 */}
                        <th className="py-1 pr-2 font-medium">example</th>
                        <th className="py-1 pr-2 font-medium">definition</th>
                        <th className="py-1 pr-2 font-medium">criteria</th>
                        {/* docs/25 §10-5 拡張: エリア/路線の役割ごと検出件数集計から除外するか
                            (電柱・電線のようにほぼ全地点で検出され比較の意味が薄い細分類向け)。 */}
                        <th className="py-1 pr-2 font-medium">比較除外</th>
                        <th className="py-1 pr-2 font-medium">評価の極性</th>
                        {/* optional 列: gqlCodes / chainRules 等、マスタ (JSON フォーマット) ごとに
                            異なりうるため列名をハードコードせず動的に描画する。 */}
                        {editing.optionalColumnKeys.map((key) => (
                          <th key={key} className="py-1 pr-2 font-medium">
                            <div className="flex items-center gap-1">
                              <span>{key}</span>
                              <button
                                type="button"
                                onClick={() => removeOptionalColumn(key)}
                                className="text-slate-400 hover:text-danger-600"
                                aria-label={`列「${key}」を削除`}
                              >
                                ✕
                              </button>
                            </div>
                          </th>
                        ))}
                        <th className="py-1 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editing.rows.map((row, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={row.categoryCd}
                              onChange={(e) => updateRow(i, { categoryCd: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="cat-a"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={row.category}
                              onChange={(e) => updateRow(i, { category: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="カテゴリA"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={row.majorCd ?? ''}
                              onChange={(e) => updateRow(i, { majorCd: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="(任意)"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <input
                              type="text"
                              value={row.major ?? ''}
                              onChange={(e) => updateRow(i, { major: e.target.value })}
                              className={INPUT_CLASS}
                              placeholder="(任意)"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <ColorSwatch
                              value={row.color}
                              onChange={(c) => updateRow(i, { color: c })}
                              ariaLabel={`行 ${i + 1} 色 (color)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <textarea
                              value={row.example ?? ''}
                              onChange={(e) => updateRow(i, { example: e.target.value })}
                              rows={2}
                              className={`${INPUT_CLASS} resize-y min-w-[160px]`}
                              placeholder="該当例 (AI プロンプトに「例: …」として埋込)"
                              aria-label={`行 ${i + 1} 該当例 (example)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <textarea
                              value={row.definition ?? ''}
                              onChange={(e) => updateRow(i, { definition: e.target.value })}
                              rows={2}
                              className={`${INPUT_CLASS} resize-y min-w-[200px]`}
                              placeholder="カテゴリ定義文 (AI への詳細説明)"
                              aria-label={`行 ${i + 1} 定義 (definition)`}
                            />
                          </td>
                          <td className="py-1 pr-2">
                            <textarea
                              value={row.criteria ?? ''}
                              onChange={(e) => updateRow(i, { criteria: e.target.value })}
                              rows={2}
                              className={`${INPUT_CLASS} resize-y min-w-[200px]`}
                              placeholder="採用基準 (AI への判定指示、「criteria: …」として埋込)"
                              aria-label={`行 ${i + 1} 判断基準 (criteria)`}
                            />
                          </td>
                          {/* docs/25 §10-5 拡張: 比較除外フラグ。ONにするとエリア/路線の役割ごと
                              検出件数集計から、この細分類だけがスキップされる (同じ大分類の他の
                              細分類は通常通りカウントされる)。 */}
                          <td className="py-1 pr-2 text-center">
                            <input
                              type="checkbox"
                              checked={row.excludeFromAggregation ?? false}
                              onChange={(e) =>
                                updateRow(i, { excludeFromAggregation: e.target.checked })
                              }
                              aria-label={`行 ${i + 1} 比較除外`}
                              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-2 focus:ring-offset-1 focus:ring-primary-400"
                            />
                          </td>
                          {/* docs/27 §9: この行が「評価」の値 (ポジ / ネガ / 中立) を表すときの極性。
                              机上調査の評価内訳の集計・バッジ色に使う。通常の分類値は未設定のまま。 */}
                          <td className="py-1 pr-2">
                            <select
                              value={row.polarity ?? ''}
                              onChange={(e) =>
                                updateRow(i, {
                                  polarity: (e.target.value || undefined) as
                                    | 'positive'
                                    | 'negative'
                                    | 'neutral'
                                    | undefined,
                                })
                              }
                              aria-label={`行 ${i + 1} 評価の極性`}
                              className={`${INPUT_CLASS} min-w-[7rem]`}
                              style={{ maxWidth: '100%' }}
                            >
                              <option value="">—</option>
                              <option value="positive">ポジティブ</option>
                              <option value="negative">ネガティブ</option>
                              <option value="neutral">中立</option>
                            </select>
                          </td>
                          {/* optional 列: 実データ駆動 (gqlCodes / chainRules 等)。値は文字列なら
                              そのまま、配列・オブジェクトは JSON テキストとして編集する。 */}
                          {editing.optionalColumnKeys.map((key) => (
                            <td key={key} className="py-1 pr-2">
                              <textarea
                                value={formatOptionalCellValue(
                                  (row as unknown as Record<string, unknown>)[key],
                                )}
                                onChange={(e) =>
                                  updateRow(i, { [key]: parseOptionalCellValue(e.target.value) })
                                }
                                rows={2}
                                className={`${INPUT_CLASS} resize-y min-w-[160px]`}
                                placeholder={key}
                                aria-label={`行 ${i + 1} ${key} (optional 列)`}
                              />
                            </td>
                          ))}
                          <td className="py-1 text-right">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeRow(i)}
                              aria-label={`行 ${i + 1} 削除`}
                            >
                              ✕
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* colorMap 編集 (colorAxis = major / majorCd の時のみ) */}
            {colorMapVisible && (
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <label className="block text-sm font-bold text-slate-700">
                    colorMap ({editing.colorMapEntries.length} 件)
                  </label>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={syncColorMapFromRows}>
                      ⟳ rows から補完
                    </Button>
                    <Button variant="secondary" size="sm" onClick={addColorMapEntry}>
                      ＋ entry 追加
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  colorAxis = {editing.colorAxis} のとき rows[] の {editing.colorAxis} 値を key
                  として参照します。
                </p>
                {editing.colorMapEntries.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    entry がありません。「⟳ rows から補完」で自動追加可能。
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-1">
                    {editing.colorMapEntries.map((entry, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={entry.key}
                          onChange={(e) => updateColorMapEntry(i, { key: e.target.value })}
                          className={`${INPUT_CLASS} flex-1`}
                          placeholder="key"
                          aria-label={`色マップ エントリ ${i + 1} キー (colorMap key)`}
                        />
                        <ColorSwatch
                          value={entry.value}
                          onChange={(c) => updateColorMapEntry(i, { value: c ?? '#888888' })}
                          showClear={false}
                          ariaLabel={`色マップ エントリ ${i + 1} 色 (colorMap color)`}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeColorMapEntry(i)}
                          aria-label={`色マップ エントリ ${i + 1} 削除`}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* docs/105 M2: chainGlobalRules 編集 (マスタ全体の連鎖グローバル制約) */}
            <div className="border-t border-slate-200 pt-3">
              <label className="block text-sm font-bold text-slate-700 mb-2">
                連鎖ルール（グローバル制約、chainGlobalRules）
              </label>
              <p className="text-xs text-slate-500 mb-2">
                マスタ全体の連鎖制約。例: 防災では一次現象 HZR-01/05/06/08/09 を連鎖先 (sub)
                に置けない、 二次災害 HZR-20 を主軸 (primary)
                に置けない、等。空欄なら保存時に書込みません (任意)。
              </p>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <label
                    className="block text-xs text-slate-600 mb-1"
                    htmlFor="m2-chain-forbidden-primary"
                  >
                    一次（主軸）に置けないコード (forbiddenAsPrimary、カンマ区切り)
                  </label>
                  <input
                    id="m2-chain-forbidden-primary"
                    type="text"
                    value={editing.chainForbiddenAsPrimary}
                    onChange={(e) =>
                      setEditing({ ...editing, chainForbiddenAsPrimary: e.target.value })
                    }
                    className={INPUT_CLASS}
                    placeholder="例: HZR-20"
                  />
                </div>
                <div>
                  <label
                    className="block text-xs text-slate-600 mb-1"
                    htmlFor="m2-chain-forbidden-sub"
                  >
                    連鎖先（sub）に置けないコード (forbiddenAsSub、カンマ区切り)
                  </label>
                  <input
                    id="m2-chain-forbidden-sub"
                    type="text"
                    value={editing.chainForbiddenAsSub}
                    onChange={(e) =>
                      setEditing({ ...editing, chainForbiddenAsSub: e.target.value })
                    }
                    className={INPUT_CLASS}
                    placeholder="例: HZR-01, HZR-05, HZR-06"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1" htmlFor="m2-chain-note">
                    全般注記 (note、任意)
                  </label>
                  <textarea
                    id="m2-chain-note"
                    value={editing.chainGlobalNote}
                    onChange={(e) => setEditing({ ...editing, chainGlobalNote: e.target.value })}
                    rows={2}
                    className={`${INPUT_CLASS} resize-y`}
                    placeholder="連鎖ルールの補足説明"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2 border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
